import { useEffect, useState, useMemo } from 'react'
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@core/contexts/AuthContext'
import { useBranding } from '@core/hooks/useBranding'
import { useModulosVisibles } from '@core/hooks/useModulosVisibles'
import { getAllModules } from '@modules/index'
import { getDoc, doc as fbDoc } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { COLLECTIONS } from '@core/lib/constants'
import NotificacionesBell from './NotificacionesBell'
import DebugFAB from './DebugFAB'
import TutorialFAB from './TutorialFAB'
import VerComoButton from './VerComoButton'
import { menuItemsVisiblesDe, filtrarItemsMenu, pasaElMenu } from '@core/lib/menuPorRol'
import { veCodigoDeModulo } from '@core/lib/permisoModulo'

const SIDEBAR_COLLAPSED_KEY = 'mb_sidebar_collapsed'
// El pie del sidebar (usuario, «Ver como», Mi cuenta, reportar, cerrar sesión)
// nace PLEGADO: sólo la fila del usuario, y se despliega con un click. Así no
// le come el alto al menú (Mariano, 27-sep-2026). Preferencia por navegador.
const SIDEBAR_USUARIO_ABIERTO_KEY = 'mb_sidebar_usuario_abierto'

// ── Icon registry ────────────────────────────────────────────────
// Mapea los nombres de icono que declaran los configs a SVG components.
// Si un módulo pide un icono que no está acá, se muestra un círculo.

const icons = {
  home: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  building: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/></svg>,
  briefcase: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
  target: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  zap: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
  users: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  clock: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  'bar-chart-2': () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
  'message-square': () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  layers: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  sliders: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>,
  gauge: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M12 2v4"/><path d="M12 18v4"/><path d="m4.93 4.93 2.83 2.83"/><path d="m16.24 16.24 2.83 2.83"/><path d="M2 12h4"/><path d="M18 12h4"/><path d="m4.93 19.07 2.83-2.83"/><path d="m16.24 7.76 2.83-2.83"/><circle cx="12" cy="12" r="4"/></svg>,
  settings: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  shield: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  brain: () => <svg viewBox="0 0 24 24" fill="none" className="w-[18px] h-[18px]"><path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="currentColor" opacity="0.7"/></svg>,
  plug: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M9 2v6"/><path d="M15 2v6"/><path d="M6 8h12v4a6 6 0 0 1-12 0V8z"/><path d="M12 18v4"/></svg>,
  'book-open': () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
  globe: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  'alert-triangle': () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  'file-text': () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>,
  'check-square': () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  'shopping-cart': () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
  award: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>,
  list: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
}

// Fallback: si no hay icono registrado, muestra un círculo
const IconFallback = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-[18px] h-[18px]"><circle cx="12" cy="12" r="4"/></svg>

function getIcon(name) {
  return icons[name] || IconFallback
}

// ── NavItem ──
const IconMenu = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
const IconLogout = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
const IconChevronLeft = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4"><polyline points="15 18 9 12 15 6"/></svg>
const IconChevronUp = ({ abierto }) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={`w-4 h-4 transition-transform duration-200 ${abierto ? 'rotate-180' : ''}`}><polyline points="18 15 12 9 6 15"/></svg>
const IconChevronRight = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4"><polyline points="9 18 15 12 9 6"/></svg>

// Params de query que distinguen ítems del sidebar que comparten el mismo
// pathname (ej. "Proyectos" → /proyectos vs "Mis proyectos" → /proyectos?mios=1).
// El NavLink nativo calcula "activo" solo por pathname e ignora el query, así
// que ambos se pintaban a la vez. Para estos keys: el ítem que lo declara está
// activo solo si la URL trae el mismo valor; el ítem que NO lo declara no está
// activo cuando la URL lo trae. Los params de FILTRO (ej. rolId en /permisos)
// no van acá → no afectan el resaltado.
const NAV_QUERY_KEYS = ['mios']

function navItemActive(to, location) {
  const [toPath, toSearch = ''] = to.split('?')
  const base = toPath.replace(/\/$/, '') || '/'
  // Match por pathname: exacto o sub-ruta (mismo criterio que NavLink end=false),
  // salvo "/" (Inicio) que matchea solo exacto.
  const pathMatch =
    location.pathname === base ||
    (base !== '/' && location.pathname.startsWith(base + '/'))
  if (!pathMatch) return false

  const toParams = new URLSearchParams(toSearch)
  const curParams = new URLSearchParams(location.search)
  for (const k of NAV_QUERY_KEYS) {
    if (toParams.has(k)) {
      if (curParams.get(k) !== toParams.get(k)) return false
    } else if (curParams.has(k)) {
      return false
    }
  }
  return true
}

function NavItem({ to, icon: Icon, label, collapsed, admin }) {
  const location = useLocation()
  const isActive = navItemActive(to, location)
  return (
    <Link
      to={to}
      title={collapsed ? label : undefined}
      aria-current={isActive ? 'page' : undefined}
      className={
        `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${collapsed ? 'justify-center' : ''} ${
          isActive
            ? 'bg-brand-accent-light text-brand-accent'
            : 'text-zinc-400 hover:bg-white/[0.04] hover:text-white'
        }`
      }
    >
      <Icon />
      {!collapsed && <span>{label}</span>}
    </Link>
  )
}

// ── NavItem con hijos (submenú config-driven) ──────────────────────────────
// Un item de menú puede declarar `children: [{label, path, icon}]` en el
// config de su módulo (primer consumidor: Conocimiento, 4-ago-2026 — pedido
// Mariano: sub-menú en el sidebar en lugar de chips arriba de la página).
// El padre es un toggle con chevron; los hijos van anidados con border-left,
// mismo lenguaje visual que Milbrands Operaciones pero neutro. Los hijos
// heredan el gate del padre (permisoModulo se declara UNA vez, en el padre).
// Abierto si el usuario lo dejó abierto (localStorage por path del padre) o
// si una ruta hija está activa. Colapsado (sidebar fino): hijos como iconos.
function NavItemConHijos({ item, collapsed, admin }) {
  const location = useLocation()
  const algunHijoActivo = item.children.some((h) => navItemActive(h.path, location))
  const storageKey = `mb_nav_open_${item.path}`
  const [openUsuario, setOpenUsuario] = useState(() => {
    if (typeof window === 'undefined') return false
    try { return window.localStorage.getItem(storageKey) === '1' } catch { return false }
  })
  const open = openUsuario || algunHijoActivo

  function toggle() {
    const next = !open
    setOpenUsuario(next)
    try { window.localStorage.setItem(storageKey, next ? '1' : '0') } catch (_) {}
  }

  if (collapsed) {
    return item.children.map((h) => (
      <NavItem key={h.path} to={h.path} icon={getIcon(h.icon)} label={h.label} collapsed admin={admin} />
    ))
  }

  const Icon = getIcon(item.icon)
  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
          algunHijoActivo ? 'text-brand-accent' : 'text-zinc-400 hover:bg-white/[0.04] hover:text-white'
        }`}
      >
        <span className="flex items-center gap-2.5"><Icon />{item.label}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="mt-0.5 ml-4 pl-2 border-l border-white/[0.08] space-y-0.5">
          {item.children.map((h) => (
            <NavItem key={h.path} to={h.path} icon={getIcon(h.icon)} label={h.label} collapsed={false} admin={admin} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Sección colapsable de menú para un módulo ──
function MenuSection({ label, items, collapsed, admin }) {
  if (!items || items.length === 0) return null
  return (
    <>
      {!collapsed && (
        <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-semibold px-3 pt-4 pb-1">
          {label}
        </div>
      )}
      {collapsed && <div className="border-t border-white/[0.04] my-2" />}
      {items.map((item, idx) => {
        // Soporte para items especiales dentro de un grupo:
        //   { type: 'divider' }                  → línea separadora
        //   { type: 'subheader', label: 'X' }    → sub-título de sección
        if (item.type === 'divider') {
          return (
            <div
              key={`div-${idx}`}
              className={`border-t border-white/[0.06] ${collapsed ? 'mx-1 my-1.5' : 'mx-3 my-2'}`}
            />
          )
        }
        if (item.type === 'subheader') {
          if (collapsed) {
            return <div key={`sub-${idx}`} className="border-t border-white/[0.04] my-1" />
          }
          return (
            <div
              key={`sub-${idx}`}
              className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium px-3 pt-3 pb-1"
            >
              {item.label}
            </div>
          )
        }
        if (Array.isArray(item.children) && item.children.length > 0) {
          return <NavItemConHijos key={item.path} item={item} collapsed={collapsed} admin={admin} />
        }
        return (
          <NavItem
            key={item.path}
            to={item.path}
            icon={getIcon(item.icon)}
            label={item.label}
            collapsed={collapsed}
            admin={admin}
          />
        )
      })}
    </>
  )
}

// ── Sección Milbrands Operaciones (toggle + items anidados) ────────────────
// Botón principal destacado (gradient violeta + chevron) que abre/cierra un
// submenú con los items internos de la consultora. Visualmente separado del
// resto del sidebar para que sea obvio que es "otra zona" (los items adentro
// son específicos del equipo Milbrands, no del cliente).
// Estado persistido en localStorage. Default cerrado.
const MILBRANDS_OPS_OPEN_KEY = 'mb_milbrands_ops_open'

// `mostrarAdmin` / `mostrarDocumentacion` llegan YA filtrados por Layout (permiso
// + menú por rol): las dos ramas de abajo los usan igual, sin decidir de nuevo.
function MilbrandsOperacionesSection({ collapsed, mostrarAdmin, moduleItems = [], mostrarDocumentacion }) {
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(MILBRANDS_OPS_OPEN_KEY) === '1'
  })

  function toggle() {
    const next = !open
    setOpen(next)
    try { window.localStorage.setItem(MILBRANDS_OPS_OPEN_KEY, next ? '1' : '0') } catch (_) {}
  }

  // Colapsado (sidebar fino): mostramos solo un divider violeta + los items
  // directos como iconos. Sin toggle (no hay espacio).
  if (collapsed) {
    return (
      <>
        <div className="mt-3 mx-1 border-t border-violet-500/30" />
        {mostrarAdmin && (
          <NavItem to="/admin" icon={getIcon('settings')} label="Admin" collapsed admin />
        )}
        {moduleItems.map(it => (
          <NavItem key={it.path} to={it.path} icon={getIcon(it.icon)} label={it.label} collapsed admin />
        ))}
        {mostrarDocumentacion && (
          <NavItem to="/documentacion" icon={getIcon('book-open')} label="Documentación" collapsed admin />
        )}
      </>
    )
  }

  return (
    <div className="mt-4 mb-2">
      {/* Botón header — gradient violeta, font bold, chevron animado */}
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl
                   bg-gradient-to-r from-violet-500/15 to-fuchsia-500/10
                   border border-violet-500/30 hover:border-violet-400/50
                   hover:from-violet-500/20 hover:to-fuchsia-500/15
                   transition-colors group"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4 text-violet-300">
            <path d="M12 2 L15 8 L22 9 L17 14 L18 21 L12 18 L6 21 L7 14 L2 9 L9 8 Z" />
          </svg>
          <span className="text-[12px] font-bold text-violet-200 uppercase tracking-wider">
            Milbrands Operaciones
          </span>
        </span>
        <svg
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          className={`w-4 h-4 text-violet-300 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Submenú — items anidados con border-left violeta + bg tenue */}
      {open && (
        <div className="mt-1 ml-2 pl-3 border-l-2 border-violet-500/30 space-y-0.5">
          {mostrarAdmin && (
            <NavItem to="/admin" icon={getIcon('settings')} label="Admin" collapsed={false} admin />
          )}
          {moduleItems.map(it => (
            <NavItem key={it.path} to={it.path} icon={getIcon(it.icon)} label={it.label} collapsed={false} admin />
          ))}
          {mostrarDocumentacion && (
            <NavItem to="/documentacion" icon={getIcon('book-open')} label="Documentación" collapsed={false} admin />
          )}
        </div>
      )}
    </div>
  )
}

// ── Layout principal ──
export default function Layout() {
  const { user, userDoc, rolDoc, getEtiqueta, isAdmin, isCliente, puede, logout } = useAuth()
  const branding = useBranding()
  const { modulosVisibles, permModulosSet } = useModulosVisibles()
  // #87 — Wizard salta el filtro item-by-item (root absoluto).
  const esWizard = userDoc?.esWizard === true
  // Menú por rol (14-oct-2026): VISIBILIDAD, no permiso — src/core/lib/menuPorRol.js.
  // El Wizard real no se filtra; simulando un rol, sí (así se previsualiza).
  const menuPermitido = useMemo(
    () => (esWizard ? null : menuItemsVisiblesDe(rolDoc, userDoc?.rolId || null)),
    [esWizard, rolDoc, userDoc?.rolId],
  )
  const esCliente = isCliente()
  const empresaId = esCliente ? userDoc?.empresaIds?.[0] : null
  const [empresaNombre, setEmpresaNombre] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })
  const [usuarioAbierto, setUsuarioAbierto] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem(SIDEBAR_USUARIO_ABIERTO_KEY) === '1'
    } catch {
      return false
    }
  })
  const etiqueta = getEtiqueta()

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0')
    } catch { /* ignore */ }
  }, [sidebarCollapsed])

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_USUARIO_ABIERTO_KEY, usuarioAbierto ? '1' : '0')
    } catch { /* catch-mudo: sin storage (modo privado, cuota) la preferencia vive sólo en memoria; el pie igual pliega y despliega */ }
  }, [usuarioAbierto])

  // Cargar nombre de empresa para vista cliente
  useEffect(() => {
    if (!empresaId) { setEmpresaNombre(null); return }
    let active = true
    ;(async () => {
      try {
        const snap = await getDoc(fbDoc(db, COLLECTIONS.EMPRESAS, empresaId))
        if (active && snap.exists()) setEmpresaNombre(snap.data().nombre || empresaId)
      } catch { /* ignore */ }
    })()
    return () => { active = false }
  }, [empresaId])

  // Armar menú dinámico agrupado por módulo, filtrado por modulosVisibles
  const { menuGroups, milbrandsOpsItems } = useMemo(() => {
    const modules = getAllModules()
    const groups = []
    // Items de módulos internos de Milbrands (milbrandsInterno: true) — se
    // agrupan bajo el subheader "Milbrands Operaciones", no en su grupo propio.
    const opsItems = []

    // Orden del sidebar: portal primero (clientes), luego core, estrategia, admin al final
    const order = ['portal', 'gestion', 'club', 'supervision', 'plataforma-operacion', 'usuarios-permisos', 'plataforma-configuracion', 'asignaciones', 'comunicaciones', 'diagnostico', 'mercadolibre', 'plataforma', 'consultoria']
    const sorted = order.map(id => modules.find(m => m.id === id)).filter(Boolean)
    // Agregar cualquier módulo no listado en order
    for (const m of modules) {
      if (!order.includes(m.id)) sorted.push(m)
    }

    for (const mod of sorted) {
      if (!mod.menuItems || mod.menuItems.length === 0) continue
      // Filtrar por modulosVisibles (dos capas: plataforma + empresa)
      if (!modulosVisibles.includes(mod.id)) continue

      // #87 — Filtrado item-by-item: cada menuItem puede declarar
      // `permisoModulo: 'X'` (código de módulo del catálogo). Si lo declara,
      // el item se muestra solo si el rol tiene 'ver' sobre ese módulo.
      // Wizard ve todo. Items sin `permisoModulo` (dividers, subheaders, o
      // items legacy todavía no migrados) pasan transparentes para no romper.
      // La regla es la misma que usa el router por ruta (`@core/lib/permisoModulo`).
      const itemsPorPermiso = mod.menuItems.filter(it => {
        if (!it || typeof it !== 'object') return true
        if (it.type === 'divider' || it.type === 'subheader') return true
        if (!it.permisoModulo) return true
        return veCodigoDeModulo(userDoc, permModulosSet, it.permisoModulo)
      })
      // Menú por rol: DESPUÉS del filtro de permisos — nunca muestra algo que el
      // permiso ocultó. Sin `menuItemsVisibles` en el rol, no cambia nada.
      const itemsFiltrados = filtrarItemsMenu(itemsPorPermiso, menuPermitido)

      // Si después de filtrar quedaron solo dividers/subheaders (o nada),
      // ocultamos el módulo entero — evita secciones vacías en el sidebar.
      const tieneItemsReales = itemsFiltrados.some(
        it => it && it.type !== 'divider' && it.type !== 'subheader'
      )
      if (!tieneItemsReales) continue

      // Módulos internos de Milbrands → al subheader "Milbrands Operaciones"
      // (los renderiza MilbrandsOperacionesSection), no a un grupo propio.
      if (mod.milbrandsInterno) {
        for (const it of itemsFiltrados) {
          if (it && it.type !== 'divider' && it.type !== 'subheader') opsItems.push(it)
        }
        continue
      }

      // Módulos admin solo se muestran si el usuario es admin
      if (mod.tipo === 'admin') {
        groups.push({ id: mod.id, label: mod.nombre, items: itemsFiltrados, admin: true })
      } else {
        groups.push({ id: mod.id, label: mod.nombre, items: itemsFiltrados })
      }
    }

    return { menuGroups: groups, milbrandsOpsItems: opsItems }
  }, [modulosVisibles, permModulosSet, userDoc, menuPermitido])

  // «Milbrands Operaciones»: lo fijo (Admin, Documentación) pasa por el mismo
  // filtro, una sola vez para las dos ramas de la sección (plegada y abierta).
  // Sin nada que mostrar, la sección no se dibuja (ni el header vacío).
  const opsMostrarAdmin = isAdmin() && pasaElMenu('/admin', menuPermitido)
  const opsMostrarDocumentacion = pasaElMenu('/documentacion', menuPermitido)
  const opsTieneItems = opsMostrarAdmin || opsMostrarDocumentacion || milbrandsOpsItems.length > 0

  const roleColors = {
    'Master Admin': 'bg-brand-accent-light text-brand-accent',
    'Líder': 'bg-blue-500/10 text-blue-400',
    'Consultor': 'bg-green-500/10 text-green-400',
    'Dueño': 'bg-purple-500/10 text-purple-400',
  }
  const roleClass = roleColors[etiqueta] || 'bg-yellow-500/10 text-yellow-400'

  // El "plegado" (íconos sin label) es una affordance SOLO de desktop: en mobile
  // el sidebar siempre se abre a ancho completo (w-[260px]). Si dejáramos que el
  // estado `sidebarCollapsed` (persistido desde desktop) ocultara los labels en
  // mobile, quedaría una barra ancha con solo íconos = ilegible. `navCollapsed`
  // fuerza la vista expandida cuando el drawer mobile está abierto.
  const navCollapsed = sidebarCollapsed && !sidebarOpen

  return (
    <div className="flex min-h-screen">
      {/* Background glows */}
      <div className="bg-glow w-[500px] h-[500px] bg-brand-accent -top-[150px] -right-[100px]" />
      <div className="bg-glow w-[400px] h-[400px] bg-brand-purple -bottom-[100px] -left-[100px]" />

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-brand-bg/90 backdrop-blur-xl border-b border-white/[0.04] px-5 py-3 z-40 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="text-white">
          <IconMenu />
        </button>
        <span className="font-bold text-base">
          <span className="bg-gradient-to-br from-brand-accent to-brand-purple bg-clip-text text-transparent">{branding.nombre}</span> {branding.subtitulo}
        </span>
        <NotificacionesBell />
      </div>

      {/* Desktop top-right — campana flotante */}
      <div className="hidden md:block fixed top-4 right-5 z-40">
        <NotificacionesBell />
      </div>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        min-h-screen bg-brand-bg md:bg-white/[0.02] border-r border-white/[0.04] flex flex-col
        fixed left-0 top-0 bottom-0 z-50 transition-[transform,width,padding] duration-300
        ${sidebarCollapsed ? 'md:w-[68px] md:px-2 md:py-4 w-[260px] p-4' : 'w-[260px] p-4'}
        md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Brand + toggle */}
        <div className={`pb-6 mb-6 border-b border-white/[0.04] flex items-center gap-2 ${sidebarCollapsed ? 'md:px-1 md:justify-center px-2 justify-between' : 'px-2 justify-between'}`}>
          <h1 className={`text-[22px] font-extrabold tracking-tight ${sidebarCollapsed ? 'md:hidden' : ''}`}>
            <span className="bg-gradient-to-br from-brand-accent to-brand-purple bg-clip-text text-transparent">
              {branding.nombre}
            </span>{' '}
            {branding.subtitulo}
          </h1>
          <button
            onClick={() => setSidebarCollapsed(c => !c)}
            className="hidden md:flex items-center justify-center w-8 h-8 rounded-lg text-zinc-400 hover:bg-white/[0.04] hover:text-white transition-colors shrink-0"
            title={sidebarCollapsed ? 'Expandir menú' : 'Plegar menú'}
            aria-label={sidebarCollapsed ? 'Expandir menú' : 'Plegar menú'}
          >
            {sidebarCollapsed ? <IconChevronRight /> : <IconChevronLeft />}
          </button>
        </div>

        {/* Empresa badge (vista cliente) */}
        {esCliente && empresaNombre && !navCollapsed && (
          <div className="mb-4 mx-1 px-3 py-2.5 bg-white/[0.02] border border-white/[0.04] rounded-lg">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Mi empresa</div>
            <div className="text-[13px] text-white font-medium truncate">{empresaNombre}</div>
          </div>
        )}

        {/* Nav dinámico — los grupos del sidebar se derivan del ROL del usuario
            (#58: useModulosVisibles → permisoModulos de cada módulo). Antes
            estaba gateado a esWizard() hasta tener las "vistas v2"; ahora cada
            usuario ve su sidebar según los permisos de su rol. */}
        <nav className="flex-1 min-h-0 space-y-0.5 overflow-y-auto overflow-x-hidden pr-1">
          {/* Inicio */}
          <NavItem to="/" icon={getIcon('home')} label="Inicio" collapsed={navCollapsed} />

          {/* Grupos de módulos */}
          {menuGroups.map(group => (
            <MenuSection
              key={group.id}
              label={group.label}
              items={group.items}
              collapsed={navCollapsed}
              admin={group.admin}
            />
          ))}

          {/* Sección "Milbrands Operaciones" — submenú destacado.
              Estilo distintivo (gradient violeta + chevron + items anidados con
              border-left) para separar la zona interna de la consultora del
              resto del sidebar (que es del cliente). Items adentro: Admin,
              Documentación, y futuros items de organización interna.
              Gating por permiso editable: `milbrandsOperaciones.ver`. */}
          {typeof puede === 'function' && puede('milbrandsOperaciones', 'ver') && opsTieneItems && (
            <MilbrandsOperacionesSection
              collapsed={navCollapsed}
              mostrarAdmin={opsMostrarAdmin}
              moduleItems={milbrandsOpsItems}
              mostrarDocumentacion={opsMostrarDocumentacion}
            />
          )}
        </nav>

        {/* User footer */}
        {/* Plegado por default: la fila del usuario es el botón que despliega
            el resto. Las acciones se OCULTAN (no se desmontan): el grabador de
            tutoriales y el reporte de bugs conservan su estado aunque se pliegue. */}
        <div className="pt-3 border-t border-white/[0.04]">
          <button
            type="button"
            onClick={() => setUsuarioAbierto(a => !a)}
            aria-expanded={usuarioAbierto}
            aria-controls="sidebar-usuario-acciones"
            title={usuarioAbierto ? 'Plegar opciones de la cuenta' : 'Ver opciones de la cuenta'}
            className={`flex items-center gap-2.5 w-full py-2 rounded-xl text-left hover:bg-white/[0.04] transition-colors ${sidebarCollapsed ? 'md:justify-center md:px-0 px-2' : 'px-2'}`}
          >
            {user?.photoURL && (
              <img
                src={user.photoURL}
                alt=""
                className="w-8 h-8 rounded-full border-2 border-white/[0.08] shrink-0"
                title={sidebarCollapsed ? `${userDoc?.nombre || user?.displayName || ''} — ${etiqueta}` : undefined}
              />
            )}
            <div className={`min-w-0 flex-1 ${sidebarCollapsed ? 'md:hidden' : ''}`}>
              <div className="text-[13px] font-semibold truncate">{userDoc?.nombre || user?.displayName}</div>
              {usuarioAbierto && (
                <>
                  <div className="text-[11px] text-zinc-600 truncate">{user?.email}</div>
                  <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide mt-0.5 ${roleClass}`}>
                    {etiqueta}
                  </span>
                </>
              )}
            </div>
            {/* Con el menú plegado y sin foto, el chevron es lo único visible del
                botón: sin él quedaría un botón vacío y el pie, inalcanzable. */}
            <span className={`text-zinc-500 shrink-0 ${sidebarCollapsed && user?.photoURL ? 'md:hidden' : ''}`}>
              <IconChevronUp abierto={usuarioAbierto} />
            </span>
          </button>
          <div id="sidebar-usuario-acciones" hidden={!usuarioAbierto}>
          {/* Ver como — launcher staff para abrir el portal del cliente en pestaña nueva */}
          <VerComoButton collapsed={sidebarCollapsed} />
          <NavLink
            to="/mi-cuenta"
            title={sidebarCollapsed ? 'Mi cuenta' : undefined}
            className={({ isActive }) =>
              `flex items-center gap-2.5 w-full px-3 py-2.5 mt-2 rounded-xl text-sm font-medium transition-all duration-200 ${sidebarCollapsed ? 'md:justify-center' : ''} ${
                isActive
                  ? 'bg-brand-accent-light text-brand-accent'
                  : 'text-zinc-500 hover:bg-white/[0.04] hover:text-white'
              }`
            }
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[18px] h-[18px]">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 21a8 8 0 0 1 16 0"/>
            </svg>
            <span className={sidebarCollapsed ? 'md:hidden' : ''}>Mi cuenta</span>
          </NavLink>
          {/* Acciones rápidas — reportar bug (DebugFAB) + grabar tutorial
              (TutorialFAB) como íconos chicos. Antes eran botones flotantes
              grandes (bottom-right); ahora viven acá, donde estaba el selector
              de rol. Cada uno conserva su propio gate de permisos. */}
          <div className={`mt-2 flex items-center gap-1.5 ${navCollapsed ? 'md:flex-col md:gap-2 justify-center' : 'px-2'}`}>
            <DebugFAB variant="inline" />
            <TutorialFAB variant="inline" />
          </div>
          <button
            onClick={logout}
            title={sidebarCollapsed ? 'Cerrar sesión' : undefined}
            className={`flex items-center gap-2.5 w-full px-3 py-2.5 mt-1 rounded-xl text-sm font-medium text-zinc-500 hover:bg-white/[0.04] hover:text-white transition-all duration-200 ${sidebarCollapsed ? 'md:justify-center' : ''}`}
          >
            <IconLogout />
            <span className={sidebarCollapsed ? 'md:hidden' : ''}>Cerrar sesión</span>
          </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      {/* min-w-0 critical: sin esto, flex-1 respeta el min-content de sus hijos.
          Si algún hijo (ej. una tabla con muchas columnas) excede el viewport,
          el main se expande y arrastra TODO el contenido fuera de pantalla,
          incluyendo grids de KPIs que ya están bien dimensionados. Con min-w-0
          el main se contrae al ancho disponible y los hijos con overflow
          (tablas) deben tener su propio scroll horizontal. */}
      <main className={`${sidebarCollapsed ? 'md:ml-[68px]' : 'md:ml-[260px]'} flex-1 min-w-0 p-5 pt-20 md:p-10 relative z-10 transition-[margin] duration-300`}>
        <Outlet />
      </main>
    </div>
  )
}
